import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import Footer from './components/Footer';

// Components
import Header from './components/Header';
import ActivityFeed from './components/ActivityFeed';
import PWAInstallBanner from './components/PWAInstallBanner';
import CookieBanner from './components/CookieBanner';
import EmailVerificationBanner from './components/EmailVerificationBanner';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import ActivityDetail from './pages/ActivityDetail';
import CreatorProfile from './pages/CreatorProfile';
import CreatorDashboard from './pages/CreatorDashboard';
import Profile from './pages/Profile';
import MyActivities from './pages/MyActivities';
import Notifications from './pages/Notifications';
import Legal from './pages/Legal';
import Help from './pages/Help';
import Checkout from './pages/Checkout';
import PaymentProof from './pages/PaymentProof';
import Messages from './pages/Messages';
import Contact from './pages/Contact';
import ForgotPassword from './pages/ForgotPassword';
import AdminPanel from './pages/AdminPanel';
import NotFound from './pages/NotFound';
import VerifyEmail from './pages/VerifyEmail';
import Landing from './pages/Landing';

import './App.css';

// Amplitude Analytics + Session Replay (only if user consented)
import { getCookieConsent } from './components/CookieBanner';

// Amplitude is initialized via CDN in index.html (with engagement plugin)
// Here we only handle opt-out based on cookie consent
const consent = getCookieConsent();
if (consent === 'declined' && window.amplitude) {
  window.amplitude.setOptOut(true);
}

// Protected Route wrapper
const AuthRedirectLanding = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (isAuthenticated) return <Navigate to="/prelanzamiento" replace />;
  return <Landing />;
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Creator Route wrapper
const CreatorRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || user.role !== 'creator') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Email verification banner
const VerifyEmailBanner = () => {
  const { user } = useAuth();
  const location = useLocation();

  // Don't show on auth pages or verify page
  const hiddenPaths = ['/login', '/register', '/verify-email', '/forgot-password', '/auth/callback'];
  if (hiddenPaths.some(p => location.pathname.startsWith(p))) return null;
  if (!user || user.email_verified !== false) return null;
  // Google users are auto-verified
  if (user.auth_provider === 'google') return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center" data-testid="verify-email-banner">
      <p className="text-xs text-amber-700">
        Verifica tu correo para acceder a todas las funciones.{' '}
        <Link to={`/verify-email?email=${encodeURIComponent(user.email)}`} className="font-semibold underline">
          Verificar ahora
        </Link>
      </p>
    </div>
  );
};

// Layout with Header
const Layout = ({ children, showHeader = true }) => {
  return (
    <>
      {showHeader && <Header />}
      {showHeader && <div className="h-14 md:h-11" />}
      <VerifyEmailBanner />
      {children}
      <Footer />
    </>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for OAuth callback - process before rendering normal routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Landing Page - redirect to prelanzamiento if logged in */}
      <Route path="/" element={<AuthRedirectLanding />} />
      
      {/* Pre-launch App Access */}
      <Route path="/prelanzamiento" element={<Layout><ActivityFeed /></Layout>} />
      
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/activity/:id" element={<Layout showHeader={false}><ActivityDetail /></Layout>} />
      <Route path="/creator/:id" element={<Layout><CreatorProfile /></Layout>} />
      <Route path="/legal" element={<Layout showHeader={false}><Legal /></Layout>} />
      <Route path="/help" element={<Layout showHeader={false}><Help /></Layout>} />
      <Route path="/contact" element={<Layout showHeader={false}><Contact /></Layout>} />
      
      {/* Protected Routes */}
      <Route path="/checkout/:id" element={
        <ProtectedRoute>
          <Checkout />
        </ProtectedRoute>
      } />
      <Route path="/payment-proof/:activityId" element={
        <ProtectedRoute>
          <PaymentProof />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout showHeader={false}><Profile /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/my-activities" element={
        <ProtectedRoute>
          <Layout><MyActivities /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <Layout><Notifications /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Layout><Messages /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Creator Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <CreatorRoute>
            <Layout><CreatorDashboard /></Layout>
          </CreatorRoute>
        </ProtectedRoute>
      } />
      
      {/* Admin Route */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <Layout><AdminPanel /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Fallback - 404 */}
      <Route path="*" element={<Layout showHeader={false}><NotFound /></Layout>} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <div className="App min-h-screen bg-white" style={{ overflowX: 'clip' }}>
              <EmailVerificationBanner />
              <AppRouter />
              <PWAInstallBanner />
              <CookieBanner />
              <Toaster position="top-center" richColors />
            </div>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
